import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '$lib/components/ui/Skeleton';
import type { SignalEventDTO } from '$lib/api/adminScraper';
import { formatKr } from '$lib/lib/formatters';
import { cn } from '$lib/lib/cn';

export interface DivergencePanelProps {
  signals: SignalEventDTO[];
  loading: boolean;
}

// Render the per-portal rents inline. The payload shape is documented as
// `{portals: [{portal, rent}], spread_pct}`, but every field is optional —
// readers fall back to the event summary string when payload is missing so
// a backend payload migration doesn't blank the panel.
function PortalRents({ payload }: { payload: SignalEventDTO['payload'] }) {
  if (!payload?.portals?.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-2xs text-fg-muted">
      {payload.portals.map((p, i) => (
        <span key={`${p.portal}-${i}`} className="flex items-center gap-1">
          <span className="text-fg-subtle">{p.portal}</span>
          <span className="num text-fg">{p.rent != null ? formatKr(p.rent) : '—'}</span>
        </span>
      ))}
    </div>
  );
}

function spreadCopy(payload: SignalEventDTO['payload'], t: (k: string, v: Record<string, unknown>) => string): string | null {
  if (typeof payload?.spread_pct !== 'number') return null;
  const sign = payload.sign ?? (payload.spread_pct >= 0 ? '+' : '-');
  return t('admin.divergence.spread', {
    sign,
    percent: Math.abs(payload.spread_pct).toFixed(1)
  });
}

export function DivergencePanel({ signals, loading }: DivergencePanelProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <section className="px-4 pb-6" data-testid="divergence-panel">
      <header className="flex flex-col gap-0.5 pb-2">
        <h2 className="text-sm font-semibold text-fg">{t('admin.divergence.title')}</h2>
        <p className="text-2xs text-fg-muted">{t('admin.divergence.subtitle')}</p>
      </header>

      <div className="rounded border border-rule bg-surface">
        {loading && signals.length === 0 && (
          <ul className="divide-y divide-rule" aria-busy>
            {Array.from({ length: 3 }, (_, i) => (
              <li key={i} className="flex flex-col gap-1 px-3 py-2">
                <Skeleton width="55%" height={12} />
                <Skeleton width="35%" height={10} muted />
              </li>
            ))}
          </ul>
        )}

        {!loading && signals.length === 0 && (
          <p className="px-3 py-6 text-center text-2xs text-fg-subtle" data-testid="divergence-empty">
            {t('admin.divergence.empty')}
          </p>
        )}

        {!loading && signals.length > 0 && (
          <ul className="divide-y divide-rule">
            {signals.map((sig) => {
              const unit = sig.unit;
              const spread = spreadCopy(sig.payload, t);
              const rowAriaLabel = unit
                ? t('admin.divergence.rowAriaLabel', { address: unit.address })
                : sig.summary;
              return (
                <li
                  key={sig.id}
                  className={cn(
                    'flex cursor-pointer items-center justify-between gap-3 px-3 py-2 text-xs',
                    unit ? 'hover:bg-surface-2 focus-within:bg-surface-2' : 'opacity-80'
                  )}
                  onClick={() => unit && navigate(`/units/${unit.id}`)}
                  onKeyDown={(e) => {
                    if (!unit) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/units/${unit.id}`);
                    }
                  }}
                  tabIndex={unit ? 0 : -1}
                  role={unit ? 'link' : undefined}
                  aria-label={rowAriaLabel}
                  data-testid={`divergence-row-${sig.id}`}
                >
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="truncate text-fg">{unit?.address ?? sig.summary}</span>
                    <PortalRents payload={sig.payload} />
                  </div>
                  {spread && (
                    <span className="num shrink-0 rounded-xs bg-warning/15 px-1.5 py-0.5 text-2xs font-medium text-warning">
                      {spread}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
